import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Stethoscope, Clock, Shield, Star, ArrowRight, Sparkles, Calendar, Video, Phone } from 'lucide-react';
import './Hero.css';

const Hero = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Popular search suggestions
  const suggestions = [
    { text: 'सिरदर्द (Headache)', category: 'Neurology', icon: Stethoscope },
    { text: 'पेट दर्द (Stomach ache)', category: 'Gastroenterology', icon: Stethoscope },
    { text: 'सीने में दर्द (Chest pain)', category: 'Cardiology', icon: Stethoscope },
    { text: 'बुखार (Fever)', category: 'General Medicine', icon: Stethoscope },
    { text: 'कमर दर्द (Back pain)', category: 'Orthopedics', icon: Stethoscope },
    { text: 'त्वचा पर चकत्ते (Skin rash)', category: 'Dermatology', icon: Stethoscope }
  ];

  // Filter suggestions based on search query
  const filteredSuggestions = suggestions.filter(suggestion =>
    suggestion.text.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSearch = async (query = searchQuery) => {
    if (!query.trim()) return;
    
    setIsSearching(true);
    setShowSuggestions(false);
    
    // Simulate search delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Navigate to search results
    navigate('/find-doctors', { 
      state: { searchQuery: query },
      replace: false 
    });
    
    setIsSearching(false);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.text);
    handleSearch(suggestion.text);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Trust indicators data
  const trustStats = [
    { number: '1L+', label: 'Patients Helped', icon: Shield },
    { number: '5K+', label: 'Expert Doctors', icon: Stethoscope },
    { number: '99.9%', label: 'Success Rate', icon: Star },
    { number: '24/7', label: 'Emergency Care', icon: Clock }
  ];

  return (
    <section className="hero">
      <div className="hero-background">
        <div className="medical-pattern"></div>
        <div className="floating-elements">
          <div className="floating-icon floating-icon-1">
            <Stethoscope size={24} />
          </div>
          <div className="floating-icon floating-icon-2">
            <Shield size={20} />
          </div>
          <div className="floating-icon floating-icon-3">
            <Star size={18} />
          </div>
        </div>
      </div>

      <div className="container">
        <div className="hero-content">
          {/* Left Side - Content */}
          <div className="hero-left">
            {/* Main Heading */}
            <div className="hero-heading">
              <h1>
                Find the Right Doctor
                <span className="highlight"> for You</span>
              </h1>
              <p className="hero-subtitle">
                Get matched with the right doctor near you in seconds. Our AI-powered platform connects you with verified healthcare professionals across India.
              </p>
            </div>

            {/* Search Section */}
            <div className="search-section">
              <div className={`search-container ${searchFocused ? 'focused' : ''}`}>
                <div className="search-bar">
                  <div className="search-input-wrapper">
                    <Search className="search-icon" size={20} />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => {
                        setSearchFocused(true);
                        setShowSuggestions(true);
                      }}
                      onBlur={() => {
                        setTimeout(() => setSearchFocused(false), 200);
                        setTimeout(() => setShowSuggestions(false), 200);
                      }}
                      onKeyPress={handleKeyPress}
                      placeholder="Search symptoms, conditions, or doctor names (Hindi/English)..."
                      className="search-input"
                    />
                  </div>
                  <button
                    onClick={() => handleSearch()}
                    disabled={isSearching || !searchQuery.trim()}
                    className={`search-btn ${isSearching ? 'searching' : ''}`}
                  >
                    {isSearching ? (
                      <>
                        <div className="search-spinner"></div>
                        <span>Searching...</span>
                      </>
                    ) : (
                      <>
                        <span>Search</span>
                        <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </div>

                {/* Search Suggestions */}
                {showSuggestions && filteredSuggestions.length > 0 && (
                  <div className="search-suggestions">
                    <div className="suggestions-header">
                      <Sparkles size={16} />
                      <span>Popular searches</span>
                    </div>
                    <div className="suggestions-list">
                      {filteredSuggestions.map((suggestion, index) => {
                        const IconComponent = suggestion.icon;
                        return (
                          <button
                            key={index}
                            onClick={() => handleSuggestionClick(suggestion)}
                            className="suggestion-item"
                          >
                            <IconComponent size={16} />
                            <div className="suggestion-content">
                              <span className="suggestion-text">{suggestion.text}</span>
                              <span className="suggestion-category">{suggestion.category}</span>
                            </div>
                            <ArrowRight size={14} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="hero-actions">
              <button 
                onClick={() => navigate('/book-appointment')}
                className="btn-primary hero-btn"
              >
                <Calendar size={18} />
                <span>Book Appointment</span>
              </button>
              <button 
                onClick={() => navigate('/telemedicine')}
                className="btn-secondary hero-btn"
              >
                <Video size={18} />
                <span>Start Video Call</span>
              </button>
              <button 
                onClick={() => navigate('/emergency')}
                className="btn-emergency hero-btn"
              >
                <Phone size={18} />
                <span>Emergency Care</span>
              </button>
            </div>
          </div>

          {/* Right Side - Visual Elements */}
          <div className="hero-right">
            {/* Medical Illustration */}
            <div className="hero-illustration">
              <div className="illustration-card">
                <div className="card-header">
                  <div className="doctor-avatar">
                    <Stethoscope size={32} />
                  </div>
                  <div className="card-info">
                    <h3>Dr. Priya Sharma</h3>
                    <p>Cardiologist • 4.9★ Rating</p>
                  </div>
                </div>
                <div className="card-stats">
                  <div className="stat">
                    <span className="stat-number">15+</span>
                    <span className="stat-label">Years Experience</span>
                  </div>
                  <div className="stat">
                    <span className="stat-number">2.5K+</span>
                    <span className="stat-label">Patients Helped</span>
                  </div>
                </div>
                <div className="card-actions">
                  <button className="card-btn primary">
                    <Calendar size={16} />
                    Book Now
                  </button>
                  <button className="card-btn secondary">
                    <Video size={16} />
                    Video Call
                  </button>
                </div>
              </div>
              
              {/* Floating Elements */}
              <div className="floating-cards">
                <div className="floating-card card-1">
                  <Shield size={20} />
                  <span>Data Secure</span>
                </div>
                <div className="floating-card card-2">
                  <Clock size={20} />
                  <span>24/7 Available</span>
                </div>
                <div className="floating-card card-3">
                  <Star size={20} />
                  <span>Verified Doctors</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Indicators - Full Width */}
        <div className="trust-indicators">
          {trustStats.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div key={index} className="trust-item">
                <div className="trust-icon">
                  <IconComponent size={24} />
                </div>
                <div className="trust-content">
                  <div className="trust-number">{stat.number}</div>
                  <div className="trust-label">{stat.label}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Hero;